/**
 * Texto longo — nativo, e não pela ponte.
 *
 * ## Por que saiu da ponte
 *
 * O widget legado usa `[nzAutosize]="{ minRows, maxRows }"`, e o `nzAutosize`
 * do NG-Zorro **escreve `height` inline em pixels**, recalculado a partir do
 * conteúdo. Estilo inline vence folha de estilo: nenhuma regra de CSS consegue
 * fazer o campo acompanhar a altura que o layout reservou. Era por isso que
 * "altura em linhas" reservava o espaço na grade e o campo continuava do
 * mesmo tamanho, com um buraco embaixo.
 *
 * Aqui a altura tem uma dona só: **as linhas visíveis** (`minRows`) definem a
 * altura natural, e quando o layout pede mais de uma linha da grade o campo
 * estica para preencher. Sem briga, e sem cálculo em JavaScript.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { TranslateService } from '@ngx-translate/core';

import { ScreenNode } from '../../model/node-props';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { SCREEN_RUNTIME } from '../../runtime/screen-runtime';

/** Linhas visíveis quando o usuário não escolheu. */
const DEFAULT_ROWS = 3;

@Component({
  selector: 'tem-textarea-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NzFormModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (form(); as group) {
      <div class="tem-textarea" [formGroup]="group">
        @if (label(); as text) {
          <label class="tem-textarea__label" [attr.for]="controlName()">
            {{ text }}
            @if (required()) {
              <span class="tem-textarea__required">*</span>
            }
          </label>
        }

        <textarea
          class="tem-textarea__input"
          nz-input
          [id]="controlName()"
          [formControlName]="controlName()"
          [placeholder]="placeholder()"
          [readOnly]="readonly()"
          [rows]="rows()"
          (blur)="onBlur()"></textarea>
      </div>
    }
  `,
  styles: [`
    :host { display: block; height: 100%; }

    .tem-textarea {
      display: flex;
      flex-direction: column;
      gap: 4px;
      height: 100%;
    }

    .tem-textarea__label {
      font-size: var(--tem-fs-sm);
      color: var(--tem-ink);
    }

    .tem-textarea__required {
      color: var(--tem-danger);
      margin-inline-start: 2px;
    }

    /* O atributo rows dá a altura natural; o flex faz o campo preencher quando
       o layout reservou mais de uma linha da grade. Os dois convivem porque
       nenhum deles escreve height inline — que era exatamente o que o
       nzAutosize fazia, e o que impedia o campo de crescer. */
    .tem-textarea__input {
      flex: 1 1 auto;
      min-height: 0;
      resize: vertical;
    }
  `]
})
export class TextAreaFieldRenderer implements NodeRenderer<'textarea'> {

  readonly node = input.required<ScreenNode<'textarea'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly translate = inject(TranslateService);
  private readonly runtime = inject(SCREEN_RUNTIME, { optional: true });

  private readonly props = computed(() => this.node().props);

  readonly controlName = computed<string>(() => this.node().id);

  readonly label = computed<string>(() => this.text(this.props().labelKey));

  readonly placeholder = computed<string>(() => this.text(this.props().placeholderKey));

  readonly required = computed<boolean>(() => this.props().required === true);

  readonly readonly = computed<boolean>(() => this.props().readonly === true);

  /**
   * As linhas visíveis.
   *
   * Mínimo de um: `rows="0"` faz o navegador cair no padrão dele (dois), e o
   * usuário que pediu zero veria dois sem entender por quê.
   */
  readonly rows = computed<number>(() => {
    const escolhido = this.props().minRows;

    return typeof escolhido === 'number' && escolhido >= 1
      ? Math.floor(escolhido)
      : DEFAULT_ROWS;
  });

  /** Sair do campo é gatilho `onBlur`. No editor não dispara. */
  onBlur(): void {
    if (this.mode() === 'editPreview') {
      return;
    }

    this.runtime?.trigger(this.node().id, 'onBlur');
  }

  private text(key: string | undefined): string {
    return typeof key === 'string' && key.length > 0
      ? translateTAValue(this.translate, key)
      : '';
  }
}
